import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type ValidationFinding } from "@/lib/dashApi";
import { ErrorState, LoadingState, PageHeader, Panel } from "@/components/dash/ui";

const RESULT_TONE: Record<string, string> = {
  match: "text-emerald-300 bg-emerald-500/10",
  mismatch: "text-rose-300 bg-rose-500/10",
  partial: "text-amber-300 bg-amber-500/10",
  unavailable: "text-slate-400 bg-slate-500/10",
};

export default function ValidationPage() {
  const kpis = useQuery({ queryKey: ["validation-kpis"], queryFn: dashApi.validationKpis });
  const alerts = useQuery({ queryKey: ["validation-alerts"], queryFn: dashApi.validationAlerts });
  const notif = useQuery({ queryKey: ["validation-notifications"], queryFn: dashApi.validationNotifications });
  const esc = useQuery({ queryKey: ["validation-escalations"], queryFn: dashApi.validationEscalations });
  const links = useQuery({ queryKey: ["validation-deep-links"], queryFn: dashApi.validationDeepLinks });

  return (
    <div className="space-y-6">
      <PageHeader
        title="KPI & System Validation"
        description="Source-backed, read-only correctness checks. Each KPI is compared to an INDEPENDENT recompute against its source (match / mismatch / partial / unavailable). Results are evidence — nothing is auto-confirmed. Invariants verify the honesty rules (expected ≠ settled, repair value separate, companies never merged)."
      />

      <Panel
        title="KPI validation"
        subtitle={kpis.data ? `${kpis.data.counts.match ?? 0} match · ${kpis.data.counts.mismatch ?? 0} mismatch · ${kpis.data.counts.partial ?? 0} partial · ${kpis.data.counts.unavailable ?? 0} unavailable` : undefined}
      >
        {kpis.isLoading ? <LoadingState /> : kpis.isError ? <ErrorState error={kpis.error} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="pb-2 pr-3">Result</th>
                  <th className="pb-2 pr-3">Metric</th>
                  <th className="pb-2 pr-3">Source</th>
                  <th className="pb-2 pr-3">Comparison</th>
                  <th className="pb-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {(kpis.data?.findings ?? []).map((f: ValidationFinding) => (
                  <tr key={f.key} className="border-t border-slate-800 align-top">
                    <td className="py-1.5 pr-3">
                      <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${RESULT_TONE[f.result] ?? "text-slate-300"}`}>{f.result}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-slate-200">
                      {f.label}
                      {f.category === "invariant" && <span className="ml-1 text-[10px] text-violet-300">invariant</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-500">{f.source}</td>
                    <td className="py-1.5 pr-3 text-slate-400">{f.comparison || "—"}</td>
                    <td className="py-1.5 text-slate-500">{f.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Notification delivery quality">
          {notif.isError ? <ErrorState error={notif.error} /> : notif.data ? (
            <dl className="space-y-1.5 text-sm">
              <Row k="Total deliveries" v={notif.data.total} />
              <Row k="By status" v={Object.entries(notif.data.by_status).map(([s, n]) => `${s}:${n}`).join(" · ") || "—"} />
              <Row k="Retry cap" v={notif.data.retry_cap} />
              <Row k="Over cap" v={notif.data.deliveries_over_cap} tone={notif.data.deliveries_over_cap ? "rose" : "emerald"} />
              <Row k="Dedupe violations" v={notif.data.dedupe_violations} tone={notif.data.dedupe_violations ? "rose" : "emerald"} />
              <Row k="Verdict" v={notif.data.reason} tone={notif.data.verdict === "ok" ? "emerald" : "amber"} />
            </dl>
          ) : <LoadingState />}
        </Panel>
        <Panel title="Escalation quality">
          {esc.isError ? <ErrorState error={esc.error} /> : esc.data ? (
            <dl className="space-y-1.5 text-sm">
              <Row k="Total" v={esc.data.total} />
              <Row k="Active" v={esc.data.active} />
              <Row k="By status" v={Object.entries(esc.data.by_status).map(([s, n]) => `${s}:${n}`).join(" · ") || "—"} />
              <Row k="Active dedupe violations" v={esc.data.active_dedupe_violations} tone={esc.data.active_dedupe_violations ? "rose" : "emerald"} />
              <Row k="Verdict" v={esc.data.reason} tone={esc.data.verdict === "ok" ? "emerald" : "amber"} />
            </dl>
          ) : <LoadingState />}
        </Panel>
        <Panel title="Deep-link correctness" subtitle={links.data ? `${links.data.checked} link(s) checked` : undefined}>
          {links.isError ? <ErrorState error={links.error} /> : links.data ? (
            links.data.broken.length ? (
              <ul className="text-sm text-rose-300">{links.data.broken.map((b) => <li key={b}>{b}</li>)}</ul>
            ) : <p className="text-sm text-emerald-300">Every emitted deep link resolves to a known route.</p>
          ) : <LoadingState />}
        </Panel>
        <Panel title="Alert usefulness" subtitle={alerts.data ? `${alerts.data.open_count} open across ${alerts.data.rules_firing} rule(s)` : undefined}>
          {alerts.isError ? <ErrorState error={alerts.error} /> : alerts.data ? (
            <>
              {alerts.data.possibly_noisy_rules.length > 0 && (
                <p className="mb-2 text-xs text-amber-300">Possibly noisy (≥5 firing): {alerts.data.possibly_noisy_rules.join(", ")}</p>
              )}
              <ul className="space-y-1 text-xs">
                {alerts.data.findings.filter((f) => f.signals.length).slice(0, 12).map((f, i) => (
                  <li key={i} className="text-slate-400">
                    <span className="text-slate-300">{f.rule_code}</span> — {f.signals.join(", ")}
                  </li>
                ))}
                {alerts.data.findings.every((f) => !f.signals.length) && <li className="text-emerald-300">No usefulness concerns detected.</li>}
              </ul>
            </>
          ) : <LoadingState />}
        </Panel>
      </div>

      <AlertTuningPanel />
    </div>
  );
}

function Row({ k, v, tone }: { k: string; v: React.ReactNode; tone?: "rose" | "emerald" | "amber" }) {
  const c = tone === "rose" ? "text-rose-300" : tone === "emerald" ? "text-emerald-300" : tone === "amber" ? "text-amber-300" : "text-slate-200";
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-400">{k}</dt>
      <dd className={`text-right font-medium ${c}`}>{v}</dd>
    </div>
  );
}

function AlertTuningPanel() {
  const qc = useQueryClient();
  const ov = useQuery({ queryKey: ["alert-tuning-overview"], queryFn: dashApi.alertTuningOverview });
  const [reason, setReason] = useState("");
  const mut = useMutation({
    mutationFn: ({ code, body }: { code: string; body: Parameters<typeof dashApi.setAlertTuning>[1] }) => dashApi.setAlertTuning(code, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-tuning-overview"] }),
  });
  const reset = useMutation({
    mutationFn: (code: string) => dashApi.resetAlertTuning(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-tuning-overview"] }),
  });

  return (
    <Panel
      title="Alert tuning (admin)"
      subtitle="Adjust an existing rule's threshold or disable it. Every change is audited with a reason; reset reverts to the code default. Changes take effect on the next connector sync."
    >
      {ov.isLoading ? <LoadingState /> : ov.isError ? <ErrorState error={ov.error} /> : (
        <>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for change (required for audit)…"
            className="mb-3 w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1.5 text-xs text-slate-200"
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="pb-2 pr-3">Rule</th>
                  <th className="pb-2 pr-3">Threshold ({"default"})</th>
                  <th className="pb-2 pr-3">Enabled</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ov.data?.tunable.map((t) => {
                  const cur = t.tuning?.threshold ?? t.threshold_default;
                  return (
                    <tr key={t.rule_code} className="border-t border-slate-800 align-middle">
                      <td className="py-1.5 pr-3 text-slate-200">{t.label}<div className="text-[10px] text-slate-500">{t.rule_code} · {t.unit}</div></td>
                      <td className="py-1.5 pr-3">
                        <ThresholdInput
                          value={cur}
                          disabled={mut.isPending || !reason.trim()}
                          onSave={(v) => mut.mutate({ code: t.rule_code, body: { threshold: v, reason: reason || undefined } })}
                        />
                        {t.tuning?.threshold != null && <span className="ml-2 text-[10px] text-amber-400">was {t.threshold_default}</span>}
                      </td>
                      <td className="py-1.5 pr-3">
                        <button
                          onClick={() => mut.mutate({ code: t.rule_code, body: { enabled: !(t.tuning?.enabled ?? true), reason: reason || undefined } })}
                          disabled={mut.isPending || !reason.trim()}
                          className={`rounded px-2 py-0.5 text-[11px] ${(t.tuning?.enabled ?? true) ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"} disabled:opacity-40`}
                        >
                          {(t.tuning?.enabled ?? true) ? "enabled" : "disabled"}
                        </button>
                      </td>
                      <td className="py-1.5">
                        {t.tuning && (
                          <button onClick={() => reset.mutate(t.rule_code)} disabled={reset.isPending} className="text-[11px] text-slate-500 hover:text-slate-300">reset</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!reason.trim() && <p className="mt-2 text-[11px] text-amber-400">Enter a reason to enable tuning controls.</p>}
        </>
      )}
    </Panel>
  );
}

function ThresholdInput({ value, disabled, onSave }: { value: number; disabled: boolean; onSave: (v: number) => void }) {
  const [v, setV] = useState(String(value));
  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="number"
        value={v}
        onChange={(e) => setV(e.target.value)}
        className="w-20 rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-xs text-slate-200 [color-scheme:dark]"
      />
      {Number(v) !== value && (
        <button onClick={() => onSave(Number(v))} disabled={disabled} className="rounded bg-sky-500/20 px-1.5 py-0.5 text-[11px] text-sky-200 disabled:opacity-40">save</button>
      )}
    </span>
  );
}
